import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query'
import type { PaginatedResponse, PageableQuery } from '../api/types'

/**
 * Infinite-scroll data hook tailored to the doorpin BFF's pagination shape.
 *
 * The BFF returns `PaginatedResponse<T> = { items, nextPageToken, totalCount }`
 * — no explicit `hasNext` flag. We treat a non-empty `nextPageToken` as
 * "another page exists". Empty string ("") means end of results, matching
 * the Java BFF's PaginatedResponse contract.
 *
 * Why this lives in the app, not in dms-base:
 * dms-base already ships `useInfiniteScroll` typed against
 * `CursorPaginatedResponse` (with `hasNext` / `nextCursor` fields). The
 * doorpin shape is a subset; rather than reshaping every doorpin API
 * function or bloating the shared type, we adapt here.
 *
 * Extra query params (filters, sort keys) go through `params` and are
 * both:
 *   1. mixed into the queryFn call so the backend sees them, AND
 *   2. included in the queryKey so a filter change refetches page 1.
 *
 * Usage:
 *   const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
 *     useCursorList<Distributor>({
 *       queryKey: distributorKeys.list({ ... }),
 *       queryFn: (params) => listDistributors(params),
 *       pageSize: 20,
 *     })
 *   <DataTable
 *     columns={...}
 *     dataSource={items}
 *     infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
 *   />
 */
export interface UseCursorListOptions<T, P extends Record<string, unknown> = Record<string, unknown>> {
  queryKey: QueryKey
  queryFn: (params: PageableQuery & P) => Promise<PaginatedResponse<T>>
  /** Extra filter/sort params spread into every request. */
  params?: P
  pageSize?: number
  enabled?: boolean
}

export interface UseCursorListReturn<T> {
  items: T[]
  totalCount: number
  isLoading: boolean
  isError: boolean
  error: unknown
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  refetch: () => void
}

const DEFAULT_PAGE_SIZE = 20

export function useCursorList<T, P extends Record<string, unknown> = Record<string, unknown>>({
  queryKey,
  queryFn,
  params,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseCursorListOptions<T, P>): UseCursorListReturn<T> {
  const { data, isLoading, isError, error, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) =>
        queryFn({
          pageSize,
          pageToken: (pageParam as string | undefined) || undefined,
          ...(params ?? ({} as P)),
        }),
      initialPageParam: undefined as string | undefined,
      // Defensively: lastPage can be undefined if the response body was
      // malformed or the queryFn threw; nextPageToken may be null (not
      // string) if the BFF ever changes shape. Both branches must fail-safe
      // to "no more pages" — anything else crashes the whole component tree
      // via TanStack Query's observer.
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined
        const token = lastPage.nextPageToken
        return typeof token === 'string' && token.length > 0 ? token : undefined
      },
      enabled,
    })

  // Guard against BFF responses that omit `items` (empty result sets sometimes
  // return `{ totalCount: 0 }` with no items key). flatMap on undefined would
  // insert `undefined` per page, producing phantom rows in the table.
  const items = data?.pages.flatMap((p) => p.items ?? []) ?? []
  const totalCount = data?.pages[0]?.totalCount ?? 0

  return {
    items,
    totalCount,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    refetch,
  }
}
